import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { useMsalAuth } from '@/hooks/useMsalAuth';
import { Box, Paper, Typography, Button } from '@gapartners/ui';

export function LoginPage() {
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const { t } = useTranslation();
  const { login, error, isInitialized } = useMsalAuth();

  usePageMetadata({
    title: t('auth.signIn'),
    description: t('pages.auth.metaDescription'),
  });

  const handleMicrosoftLogin = async () => {
    try {
      setIsMicrosoftLoading(true);
      await login();
    } catch (err) {
      console.error('Microsoft login error', err);
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  if (!isInitialized) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <Typography>{t('pages.auth.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'primary.main',
          backgroundImage: theme =>
            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.main} 100%)`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -96,
            left: -96,
            width: 384,
            height: 384,
            bgcolor: 'common.white',
            opacity: 0.1,
            borderRadius: '50%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -120,
            right: -120,
            width: 420,
            height: 420,
            bgcolor: 'common.white',
            opacity: 0.1,
            borderRadius: '50%',
          }}
        />
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            p: { xs: 3, md: 6 },
            color: 'common.white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            maxWidth: 720,
          }}
        >
          <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
            {t('pages.auth.title')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            {t('pages.auth.leftDescription')}
          </Typography>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
            <Box component="li" sx={{ opacity: 0.9, mb: 1.5 }}>
              {t('pages.auth.bulletItem1')}
            </Box>
            <Box component="li" sx={{ opacity: 0.9, mb: 1.5 }}>
              {t('pages.auth.bulletItem2')}
            </Box>
            <Box component="li" sx={{ opacity: 0.9 }}>
              {t('pages.auth.bulletItem3')}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          px: { xs: 2, sm: 3 },
        }}
      >
        <Paper
          variant="outlined"
          sx={{ width: '100%', maxWidth: 480, p: { xs: 2.5, sm: 3 } }}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            color="text.primary"
            align="center"
          >
            {t('pages.home.title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 1 }}
          >
            {t('pages.auth.signInContactCenter')}
          </Typography>

          {error && (
            <Box
              role="alert"
              sx={{
                mt: 2,
                color: 'error.main',
                border: '1px solid',
                borderColor: 'error.light',
                bgcolor: 'error.lighter',
                borderRadius: 1,
                px: 2,
                py: 1,
                fontSize: '0.875rem',
              }}
            >
              {error}
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleMicrosoftLogin}
              disabled={isMicrosoftLoading}
              fullWidth
            >
              {isMicrosoftLoading
                ? t('auth.signIn') + '...'
                : t('pages.auth.signingIn')}
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {t('pages.auth.copyright')}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
